import {
  useEffect, useRef, useState, useCallback, useMemo, useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search, Building2, Pencil, Trash2, SlidersHorizontal, X, ChevronUp, ChevronDown, Table2, Rows3 } from 'lucide-react';
import { FormModal } from '../../components/ui/FormModal';
import { Button, Toast } from '../../components/ui/FormField';
import { Field, TextInput, SelectInput, TextareaInput, Switch, PrimaryButton, GhostButton } from '../../components/ui/Field';
import { StatsCard } from '../../components/ui/StatsCard';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { IconButton, Cell, GradientOverlayActions, PaginationControls, ActivePill } from '../../components/ui/grid';
import { useCrudPage } from '../../hooks/useCrudPage';
import { useAutoCreate } from '../../hooks/useAutoCreate';
import { formatPhone, formatPhoneInput, isValidPhone } from '../../lib/phone';
import { usePageHeader } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { useAuthStore } from '../../stores/auth-store';
import { companyApi } from '../../services/api-services';
import type { Company, ListQuery } from '../../types/api';
import { COUNTRIES, getStatesProvinces } from '../../data/geo-data';

// ─────────────────────────────────────────────────────────────
// Companies Management — Operations Deck grid layout ported onto
// the original UI (old fonts, original modal form retained).
// ─────────────────────────────────────────────────────────────

// Column templates. Comfortable (default) shows City / State-Province /
// Country as three columns; Compact collapses them into one Location column
// and tightens the rows.
// Compact: Company | First | Last | Email | Telephone | Location
// (no Actions column — actions appear as a hover overlay; columns get the room).
const COMPACT_TEMPLATE =
  'minmax(240px,1.8fr) minmax(130px,1fr) minmax(130px,1fr) minmax(240px,1.7fr) minmax(150px,1.15fr) minmax(220px,1.5fr)';
// Comfortable: Company | Active | First | Last | Email | Telephone | City | State/Province | Country | Actions
const FULL_TEMPLATE =
  'minmax(180px,1.5fr) 110px minmax(90px,0.85fr) minmax(90px,0.85fr) minmax(170px,1.4fr) minmax(104px,0.9fr) minmax(96px,0.85fr) minmax(116px,0.95fr) minmax(92px,0.8fr) 120px';
// Comfortable view has many columns; let it scroll horizontally below this width.
const FULL_MIN_WIDTH = 1210;

type CompanyView = 'comfortable' | 'compact';
const VIEW_MODE_KEY = 'companies.viewMode';
function loadCompanyView(): CompanyView {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY);
    if (v === 'compact' || v === 'comfortable') return v;
  } catch { /* ignore */ }
  return 'comfortable';
}

// State / Province → 2-letter abbreviation lookup
const STATE_ABBREV: Record<string, string> = {
  Alabama:'AL', Alaska:'AK', Arizona:'AZ', Arkansas:'AR', California:'CA',
  Colorado:'CO', Connecticut:'CT', Delaware:'DE', Florida:'FL', Georgia:'GA',
  Hawaii:'HI', Idaho:'ID', Illinois:'IL', Indiana:'IN', Iowa:'IA',
  Kansas:'KS', Kentucky:'KY', Louisiana:'LA', Maine:'ME', Maryland:'MD',
  Massachusetts:'MA', Michigan:'MI', Minnesota:'MN', Mississippi:'MS', Missouri:'MO',
  Montana:'MT', Nebraska:'NE', Nevada:'NV', 'New Hampshire':'NH', 'New Jersey':'NJ',
  'New Mexico':'NM', 'New York':'NY', 'North Carolina':'NC', 'North Dakota':'ND',
  Ohio:'OH', Oklahoma:'OK', Oregon:'OR', Pennsylvania:'PA', 'Rhode Island':'RI',
  'South Carolina':'SC', 'South Dakota':'SD', Tennessee:'TN', Texas:'TX', Utah:'UT',
  Vermont:'VT', Virginia:'VA', Washington:'WA', 'West Virginia':'WV',
  Wisconsin:'WI', Wyoming:'WY', 'District of Columbia':'DC',
  Alberta:'AB', 'British Columbia':'BC', Manitoba:'MB', 'New Brunswick':'NB',
  'Newfoundland and Labrador':'NL', 'Northwest Territories':'NT', 'Nova Scotia':'NS',
  Nunavut:'NU', Ontario:'ON', 'Prince Edward Island':'PE', Quebec:'QC',
  Saskatchewan:'SK', Yukon:'YT',
};

function abbrevState(s: string): string {
  if (!s) return '';
  const trimmed = s.trim();
  if (trimmed.length <= 2) return trimmed.toUpperCase();
  return STATE_ABBREV[trimmed] ?? trimmed.slice(0, 2).toUpperCase();
}

const COUNTRY_SHORT: Record<string, string> = {
  unitedstatesofamerica: 'USA', unitedstates: 'USA', usa: 'USA', us: 'USA', america: 'USA',
  unitedkingdom: 'UK', uk: 'UK', greatbritain: 'UK', england: 'UK',
};
function shortCountry(c: string): string {
  const key = c.toLowerCase().replace(/[^a-z0-9]/g, '');
  return COUNTRY_SHORT[key] ?? c.trim();
}

function formatLocation(city?: string, state?: string, country?: string): string {
  const parts: string[] = [];
  if (city?.trim()) parts.push(city.trim());
  if (state?.trim()) parts.push(abbrevState(state.trim()));
  const cityState = parts.join(', ');
  const cty = country?.trim() ? shortCountry(country) : '';
  if (cityState && cty) return `${cityState} · ${cty}`;
  return cityState || cty || '—';
}

type StatusFilter = 'active' | 'inactive' | 'all';

// localStorage key for the persisted Stats-strip visibility toggle.
const STATS_HIDDEN_KEY = 'companies.statsHidden';

// ── Original modal form helpers (retained) ───────────────────
const emptyCompany: Partial<Company> = {
  companyName: '', address: '', city: '',
  stateProvince: '', postalZipCode: '', country: '',
  contactPersonFirstName: '', contactPersonLastName: '', contactPersonEmail: '',
  tel: '', notes: '',
};

interface CompanyErrors {
  companyName?: string;
  address?: string;
  city?: string;
  stateProvince?: string;
  postalZipCode?: string;
  country?: string;
  contactPersonFirstName?: string;
  contactPersonLastName?: string;
  tel?: string;
  contactPersonEmail?: string;
}

function validateCompany(form: Partial<Company>): CompanyErrors {
  const e: CompanyErrors = {};
  if (!form.companyName?.trim()) e.companyName = 'Company Name is Required';
  if (!form.address?.trim()) e.address = 'Address is required';
  if (!form.city?.trim()) e.city = 'City is required';
  if (!form.stateProvince?.trim()) e.stateProvince = 'State/Province is required';
  if (!form.postalZipCode?.trim()) e.postalZipCode = 'Postal/Zip Code is required';
  if (!form.country?.trim()) e.country = 'Country is required';
  if (!form.contactPersonFirstName?.trim()) e.contactPersonFirstName = 'First Name is required';
  if (!form.contactPersonLastName?.trim()) e.contactPersonLastName = 'Last Name is required';
  // Phone — optional, but if provided must be a valid 10-digit number.
  if (!isValidPhone(form.tel)) e.tel = 'Enter a valid 10-digit phone number';
  if (!form.contactPersonEmail?.trim()) e.contactPersonEmail = 'Contact Persons Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactPersonEmail)) e.contactPersonEmail = 'Email must be a valid email address';
  return e;
}

export function CompaniesPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  // Stats strip visibility — hidden by default, persisted across visits.
  // Only an explicit "shown" choice ('0') reveals it; anything else stays hidden.
  const [statsHidden, setStatsHidden] = useState<boolean>(
    () => localStorage.getItem(STATS_HIDDEN_KEY) !== '0',
  );
  const toggleStats = () =>
    setStatsHidden((v) => {
      const next = !v;
      localStorage.setItem(STATS_HIDDEN_KEY, next ? '1' : '0');
      return next;
    });
  // Grid density — comfortable (3 location columns) vs compact (combined), persisted.
  const [viewMode, setViewModeRaw] = useState<CompanyView>(loadCompanyView);
  const setViewMode = (v: CompanyView) => {
    setViewModeRaw(v);
    try { localStorage.setItem(VIEW_MODE_KEY, v); } catch { /* ignore */ }
  };
  const compact = viewMode === 'compact';
  const [countryFilter, setCountryFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');

  const fetchFn = useCallback(
    (q: ListQuery) => (statusFilter === 'active' ? companyApi.getAllActive(q) : companyApi.getAll(q)),
    [statusFilter],
  );

  const crud = useCrudPage<Company>({ fetchFn });
  const recordActivity = useActivityStore((s) => s.record);
  const username = useAuthStore((s) => s.user?.username);

  // Stats are computed from the FULL company set (not the current page),
  // so the numbers stay stable while paging/filtering. Refreshed on CRUD.
  const [statsData, setStatsData] = useState<Company[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await companyApi.getAll({ offset: 0, limit: 1000, sortCol: 'Id', sortDirection: 'Ascending' });
      setStatsData(res.entities ?? []);
    } catch {
      /* stats are best-effort */
    } finally {
      setStatsLoading(false);
    }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Original modal form state.
  const [form, setForm] = useState<Partial<Company>>(emptyCompany);
  const [errors, setErrors] = useState<CompanyErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const openEdit = (item: Company) => {
    setForm({ ...item });
    setErrors({});
    setSubmitted(false);
    crud.openEdit(item);
  };

  const openCreate = () => {
    setForm({ ...emptyCompany });
    setErrors({});
    setSubmitted(false);
    crud.openCreate();
  };
  useAutoCreate(openCreate);

  // Publish title + primary action into the new TopBar header.
  usePageHeader(
    'Companies',
    <Button onClick={openCreate}><Plus size={16} /> Add Company</Button>,
    [],
  );

  const handleSave = async () => {
    setSubmitted(true);
    const e = validateCompany(form);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSaving(true);
    const label = form.companyName?.trim() || 'Untitled company';
    try {
      if (crud.editingItem) {
        await companyApi.update(form);
        crud.showToast('Company updated successfully');
        recordActivity({ type: 'update', entity: 'Companies', label, user: username });
      } else {
        await companyApi.create(form);
        crud.showToast('Company created successfully');
        recordActivity({ type: 'create', entity: 'Companies', label, user: username });
      }
      crud.closeModal();
      crud.load();
      loadStats();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save company';
      crud.showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name?: string) => {
    if (!confirm('Are you sure you want to delete this company?')) return;
    try {
      await companyApi.delete(id);
      crud.showToast('Company deleted');
      recordActivity({ type: 'delete', entity: 'Companies', label: name?.trim() || `Company #${id}`, user: username });
      crud.load();
      loadStats();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete company';
      crud.showToast(message, 'error');
    }
  };

  const set = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  // Client-side filter pass on the current page (country/state/city/status).
  const filteredData = useMemo(() => {
    return crud.data.filter((c) => {
      if (statusFilter === 'inactive' && !c.inactive) return false;
      if (statusFilter === 'active' && c.inactive) return false;
      if (countryFilter && (c.country ?? '').trim() !== countryFilter) return false;
      if (stateFilter && (c.stateProvince ?? '').trim() !== stateFilter) return false;
      if (cityFilter && !(c.city ?? '').toLowerCase().includes(cityFilter.toLowerCase())) return false;
      return true;
    });
  }, [crud.data, statusFilter, countryFilter, stateFilter, cityFilter]);

  const availableCountries = useMemo(() => {
    const s = new Set<string>();
    crud.data.forEach((c) => c.country?.trim() && s.add(c.country.trim()));
    return Array.from(s).sort();
  }, [crud.data]);
  const availableStates = useMemo(() => {
    const s = new Set<string>();
    crud.data.forEach((c) => {
      if (countryFilter && (c.country ?? '').trim() !== countryFilter) return;
      if (c.stateProvince?.trim()) s.add(c.stateProvince.trim());
    });
    return Array.from(s).sort();
  }, [crud.data, countryFilter]);

  const filtersActive = !!countryFilter || !!stateFilter || !!cityFilter || statusFilter !== 'active';
  const clearFilters = () => {
    setCountryFilter('');
    setStateFilter('');
    setCityFilter('');
    setStatusFilter('active');
  };

  const stats = useMemo(() => {
    const total = statsData.length;
    const active = statsData.filter((c) => !c.inactive).length;
    const inactive = statsData.filter((c) => c.inactive).length;
    const withEmail = statsData.filter((c) => !!c.contactPersonEmail?.trim()).length;
    return { total, active, inactive, withEmail };
  }, [statsData]);

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Stats strip — toggled via the Stats button, persisted in localStorage */}
      {!statsHidden && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <StatsCard label="Registered" value={stats.total} sub="Companies in portal" accent="var(--color-accent)" loading={statsLoading} />
          <StatsCard label="Active" value={stats.active} sub="Active companies" accent="var(--color-teal)" loading={statsLoading} />
          <StatsCard label="Inactive" value={stats.inactive} sub="Inactive companies" accent="var(--color-warn)" loading={statsLoading} />
          <StatsCard label="With email" value={stats.withEmail} sub="Contactable contacts" accent="#7c6ba8" loading={statsLoading} />
        </div>
      )}

      {/* Filter / search bar + grid */}
      <div
        style={{
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          width: 'fit-content',
          minWidth: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border)',
            flexWrap: 'wrap',
          }}
        >
          {/* Search */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              background: 'var(--color-panel-alt)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              width: 280,
              transition: 'border-color 180ms',
            }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search companies…"
              value={crud.searchText}
              onChange={(e) => {
                crud.setSearchText(e.target.value);
                crud.setOffset(0);
              }}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13.5,
                flex: 1,
                fontFamily: 'var(--font-body)',
                color: 'var(--color-text)',
                width: '100%',
              }}
            />
            {crud.searchText && (
              <button
                onClick={() => { crud.setSearchText(''); crud.setOffset(0); }}
                aria-label="Clear search"
                title="Clear search"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 18, height: 18, borderRadius: 9, padding: 0,
                  background: 'transparent', border: 'none',
                  color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0,
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Status — 3-way segmented control */}
          <StatusSegmented
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              crud.setOffset(0);
            }}
          />

          {/* Filter popover — Country / State / City */}
          <FilterPopover
            countryFilter={countryFilter}
            stateFilter={stateFilter}
            cityFilter={cityFilter}
            availableCountries={availableCountries}
            availableStates={availableStates}
            onChangeCountry={(v) => {
              setCountryFilter(v);
              if (v && stateFilter && availableStates.indexOf(stateFilter) === -1) setStateFilter('');
            }}
            onChangeState={setStateFilter}
            onChangeCity={setCityFilter}
            onClear={clearFilters}
            activeCount={(countryFilter ? 1 : 0) + (stateFilter ? 1 : 0) + (cityFilter ? 1 : 0)}
          />

          {filtersActive && (
            <button
              onClick={clearFilters}
              style={{
                fontSize: 12,
                color: 'var(--color-accent)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '6px 8px',
                borderRadius: 5,
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
              }}
            >
              Clear all
            </button>
          )}

          <div style={{ flex: 1 }} />

          {/* Grid density toggle — comfortable (3 location columns) vs compact */}
          <ViewModeSwitch value={viewMode} onChange={setViewMode} />

          {/* Stats toggle — light, mirrors the new-UI Well Management control */}
          <button
            type="button"
            onClick={toggleStats}
            title={statsHidden ? 'Show stats' : 'Hide stats'}
            aria-pressed={!statsHidden}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 10px',
              background: 'var(--color-panel-alt)',
              border: '1px solid var(--color-border)',
              borderRadius: 6,
              fontSize: 11,
              color: 'var(--color-text-mid)',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              letterSpacing: 0.4,
            }}
          >
            {statsHidden ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
            Stats
          </button>

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading
              ? 'Loading…'
              : filtersActive
                ? `${filteredData.length} of ${crud.data.length} on page · ${crud.totalCount} total`
                : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header + rows — horizontally scrollable so the comfortable
            (3-location-column) layout never clips on narrower screens. */}
        <div >
          {/* Header row */}
          <div
            className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
            style={{
              display: 'grid',
              gridTemplateColumns: compact ? COMPACT_TEMPLATE : FULL_TEMPLATE,
              columnGap: 0,
              minWidth: compact ? undefined : FULL_MIN_WIDTH,
              padding: 0,
              alignItems: 'stretch',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
              position: 'sticky',
              top: 72,
              zIndex: 10,
              fontSize: 14,
              letterSpacing: 0.3,
              color: 'var(--color-text)',
              fontWeight: 600,
              fontFamily: 'var(--font-body)',
            }}
          >
            <SortHeader label="Company" col="CompanyName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            {!compact && <div>Active</div>}
            <SortHeader label="First Name" col="ContactPersonFirstName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Last Name" col="ContactPersonLastName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Email" col="ContactPersonEmail" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Telephone" col="Tel" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            {compact ? (
              <SortHeader label="Location" col="City" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            ) : (
              <>
                <SortHeader label="City" col="City" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
                <SortHeader label="State/Province" col="StateProvince" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
                <SortHeader label="Country" col="Country" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
              </>
            )}
            {!compact && <div style={{ textAlign: 'right' }}>Actions</div>}
          </div>

          {/* Rows */}
          {crud.loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>
              Loading…
            </div>
          ) : filteredData.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
              <Building2 size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <div style={{ fontSize: 14, marginBottom: 4 }}>
                {filtersActive ? 'No companies match these filters' : 'No companies found'}
              </div>
              <div style={{ fontSize: 12 }}>
                {filtersActive
                  ? 'Try clearing filters or moving to another page.'
                  : 'Register a new company to get started.'}
              </div>
            </div>
          ) : (
            <div>
              {filteredData.map((c, i) => (
                <CompanyRow
                  key={c.id ?? i}
                  company={c}
                  compact={compact}
                  isLast={i === filteredData.length - 1}
                  onEdit={() => openEdit(c)}
                  onDelete={() => c.id && handleDelete(c.id, c.companyName)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination footer */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            color: 'var(--color-text-dim)',
            background: 'var(--color-panel-alt)',
            borderRadius: '0 0 10px 10px',
          }}
        >
          <div>
            Showing{' '}
            <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{filteredData.length}</span>
            {filtersActive && filteredData.length !== crud.data.length && (
              <> (of {crud.data.length} on page)</>
            )}{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span> companies
          </div>
          <PaginationControls currentPage={currentPage} totalPages={totalPages} onPage={(p) => crud.setOffset((p - 1) * crud.pageSize)} />
        </div>
      </div>

      {/* Latest-UI form — flat fields (old-UI order), only the contact
          section is a separated colored card. Active toggle in the header. */}
      <FormModal
        open={crud.modalOpen}
        onClose={crud.closeModal}
        title={crud.editingItem ? 'Edit Company' : 'New Company'}
        icon={<Building2 size={20} />}
        headerRight={
          <Switch
            checked={!form.inactive}
            onChange={(active) => setForm((p) => ({ ...p, inactive: !active }))}
            label={form.inactive ? 'Inactive' : 'Active'}
          />
        }
        footer={
          <>
            <GhostButton onClick={crud.closeModal} disabled={saving}>Cancel</GhostButton>
            <PrimaryButton onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : crud.editingItem ? 'Save changes' : 'Add Company'}
            </PrimaryButton>
          </>
        }
      >
        <Field label="Company Name" required error={submitted ? errors.companyName : undefined}>
          <TextInput
            value={form.companyName ?? ''}
            placeholder="e.g. Quantum Energy Technologies"
            error={submitted && !!errors.companyName}
            onChange={(e) => { set('companyName', e.target.value); if (submitted) setErrors(validateCompany({ ...form, companyName: e.target.value })); }}
          />
        </Field>

        <Field label="Address" required error={submitted ? errors.address : undefined}>
          <TextInput value={form.address ?? ''} placeholder="123 Energy Way" error={submitted && !!errors.address} onChange={(e) => set('address', e.target.value)} />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Country" required error={submitted ? errors.country : undefined}>
            <SelectInput
              value={form.country ?? ''}
              error={submitted && !!errors.country}
              onChange={(e) => { set('country', e.target.value); set('stateProvince', ''); }}
            >
              <option value="">-- Select Country --</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </SelectInput>
          </Field>
          <Field label="State/Province" required error={submitted ? errors.stateProvince : undefined}>
            {getStatesProvinces(form.country ?? '').length > 0 ? (
              <SelectInput
                value={form.stateProvince ?? ''}
                error={submitted && !!errors.stateProvince}
                onChange={(e) => set('stateProvince', e.target.value)}
              >
                <option value="">-- Select State/Province --</option>
                {getStatesProvinces(form.country ?? '').map((s) => <option key={s} value={s}>{s}</option>)}
              </SelectInput>
            ) : (
              <TextInput
                value={form.stateProvince ?? ''}
                error={submitted && !!errors.stateProvince}
                onChange={(e) => set('stateProvince', e.target.value)}
                placeholder="Enter State/Province"
              />
            )}
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="City" required error={submitted ? errors.city : undefined}>
            <TextInput value={form.city ?? ''} placeholder="e.g. Calgary" error={submitted && !!errors.city} onChange={(e) => set('city', e.target.value)} />
          </Field>
          <Field label="Postal/Zip" required error={submitted ? errors.postalZipCode : undefined}>
            <TextInput value={form.postalZipCode ?? ''} placeholder="e.g. T2P 1J9 / 77002" error={submitted && !!errors.postalZipCode} onChange={(e) => set('postalZipCode', e.target.value)} />
          </Field>
        </div>

        <Field label="Notes">
          <TextareaInput value={form.notes ?? ''} placeholder="Anything the team should know about this company…" onChange={(e) => set('notes', e.target.value)} />
        </Field>

        {/* Contact Person Details — separated colored card */}
        <section
          style={{
            background: 'rgba(87,154,59,0.04)',
            border: '1px solid rgba(87,154,59,0.18)',
            borderLeft: '3px solid var(--color-accent)',
            borderRadius: 10,
            padding: '16px 18px 18px',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 14 }}>
            Contact Person Details
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First Name" required error={submitted ? errors.contactPersonFirstName : undefined}>
              <TextInput
                value={form.contactPersonFirstName ?? ''}
                placeholder="e.g. Dave"
                error={submitted && !!errors.contactPersonFirstName}
                onChange={(e) => { set('contactPersonFirstName', e.target.value); if (submitted) setErrors(validateCompany({ ...form, contactPersonFirstName: e.target.value })); }}
              />
            </Field>
            <Field label="Last Name" required error={submitted ? errors.contactPersonLastName : undefined}>
              <TextInput
                value={form.contactPersonLastName ?? ''}
                placeholder="e.g. Cooper"
                error={submitted && !!errors.contactPersonLastName}
                onChange={(e) => { set('contactPersonLastName', e.target.value); if (submitted) setErrors(validateCompany({ ...form, contactPersonLastName: e.target.value })); }}
              />
            </Field>
            <Field label="Phone Number" error={submitted ? errors.tel : undefined}>
              <TextInput
                type="tel"
                inputMode="tel"
                placeholder="(XXX) XXX-XXXX"
                maxLength={14}
                value={form.tel ?? ''}
                error={submitted && !!errors.tel}
                onChange={(e) => { const v = formatPhoneInput(e.target.value); set('tel', v); if (submitted) setErrors(validateCompany({ ...form, tel: v })); }}
              />
            </Field>
            <Field label="Email" required error={submitted ? errors.contactPersonEmail : undefined}>
              <TextInput
                type="email"
                placeholder="e.g. name@example.com"
                value={form.contactPersonEmail ?? ''}
                error={submitted && !!errors.contactPersonEmail}
                onChange={(e) => { set('contactPersonEmail', e.target.value); if (submitted) setErrors(validateCompany({ ...form, contactPersonEmail: e.target.value })); }}
              />
            </Field>
          </div>
        </section>
      </FormModal>

      {crud.toast && <Toast message={crud.toast.message} type={crud.toast.type} onClose={() => crud.setToast(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Grid sub-components
// ─────────────────────────────────────────────────────────────

function CompanyRow({
  company: c,
  compact,
  isLast,
  onEdit,
  onDelete,
}: {
  company: Company;
  compact: boolean;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const active = !c.inactive;
  const statusColor = active ? 'var(--color-good)' : 'var(--color-text-dim)';
  // Concrete bg so the compact hover-overlay gradient can blend into it.
  const rowBg = hover ? 'var(--color-panel-alt)' : (compact ? 'var(--color-panel)' : 'transparent');

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
      style={{
        display: 'grid',
        gridTemplateColumns: compact ? COMPACT_TEMPLATE : FULL_TEMPLATE,
        columnGap: 0,
        minWidth: compact ? undefined : FULL_MIN_WIDTH,
        padding: 0,
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'stretch',
        fontSize: 14,
        position: compact ? 'relative' : undefined,
        background: rowBg,
        transition: 'background 120ms',
      }}
    >
      {/* Company + status — inline dot in compact; separate Active column in comfortable */}
      {compact ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span
            title={active ? 'Active' : 'Inactive'}
            style={{ width: 7, height: 7, borderRadius: 4, background: statusColor, flexShrink: 0 }}
          />
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {c.companyName ?? '—'}
          </span>
        </div>
      ) : (
        <>
          <div title={c.companyName ?? ''} style={{ minWidth: 0, fontSize: 14, fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {c.companyName ?? '—'}
          </div>
          <div><ActivePill active={active} /></div>
        </>
      )}

      <Cell ellipsis>{c.contactPersonFirstName ?? '—'}</Cell>
      <Cell ellipsis>{c.contactPersonLastName ?? '—'}</Cell>
      <Cell ellipsis>{c.contactPersonEmail || '—'}</Cell>
      <Cell ellipsis>{c.tel ? formatPhone(c.tel) : '—'}</Cell>
      {compact ? (
        <Cell ellipsis>{formatLocation(c.city, c.stateProvince, c.country)}</Cell>
      ) : (
        <>
          <Cell ellipsis>{c.city?.trim() || '—'}</Cell>
          <Cell ellipsis>{c.stateProvince?.trim() || '—'}</Cell>
          <Cell ellipsis>{c.country?.trim() || '—'}</Cell>
        </>
      )}

      {compact ? (
        <GradientOverlayActions visible={hover} bgColor={rowBg} onEdit={onEdit} onDelete={onDelete} />
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <IconButton title="Edit" onClick={onEdit}>
            <Pencil size={17} />
          </IconButton>
          <IconButton title="Delete" onClick={onDelete} hoverDanger>
            <Trash2 size={17} />
          </IconButton>
        </div>
      )}
    </div>
  );
}

// Grid density toggle — comfortable (3 location columns) vs compact (combined).
function ViewModeSwitch({ value, onChange }: { value: CompanyView; onChange: (v: CompanyView) => void }) {
  const opts: { id: CompanyView; tip: string; Icon: typeof Table2 }[] = [
    { id: 'comfortable', tip: 'Comfortable view', Icon: Table2 },
    { id: 'compact', tip: 'Compact view', Icon: Rows3 },
  ];
  return (
    <div style={{ display: 'inline-flex', padding: 3, background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)', borderRadius: 7, gap: 1 }}>
      {opts.map((o) => {
        const isActive = value === o.id;
        return (
          <button
            key={o.id}
            title={o.tip}
            aria-label={o.tip}
            aria-pressed={isActive}
            onClick={() => onChange(o.id)}
            style={{
              width: 30,
              height: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 5,
              border: 'none',
              cursor: 'pointer',
              background: isActive ? 'var(--color-accent)' : 'transparent',
              color: isActive ? '#fff' : 'var(--color-text-mid)',
              transition: 'background 120ms, color 120ms',
            }}
          >
            <o.Icon size={15} />
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Filter controls
// ─────────────────────────────────────────────────────────────

function FilterPopover({
  countryFilter,
  stateFilter,
  cityFilter,
  availableCountries,
  availableStates,
  onChangeCountry,
  onChangeState,
  onChangeCity,
  onClear,
  activeCount,
}: {
  countryFilter: string;
  stateFilter: string;
  cityFilter: string;
  availableCountries: string[];
  availableStates: string[];
  onChangeCountry: (v: string) => void;
  onChangeState: (v: string) => void;
  onChangeCity: (v: string) => void;
  onClear: () => void;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const POP_WIDTH = 340;

  const updateCoords = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const top = rect.bottom + 8;
    const desiredLeft = rect.left;
    const maxLeft = window.innerWidth - POP_WIDTH - 12;
    const left = Math.max(12, Math.min(desiredLeft, maxLeft));
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => updateCoords();
    const onResize = () => updateCoords();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updateCoords]);

  const isActive = activeCount > 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 12px',
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'var(--font-body)',
          cursor: 'pointer',
          background: open ? 'var(--color-panel-alt)' : 'var(--color-panel)',
          color: isActive ? 'var(--color-accent)' : 'var(--color-text-mid)',
          border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`,
          borderRadius: 7,
          transition: 'all 120ms',
        }}
      >
        <SlidersHorizontal size={14} />
        Filter
        {isActive && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: 'var(--color-accent)',
              color: '#fff',
              fontSize: 10.5,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}
          >
            {activeCount}
          </span>
        )}
      </button>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: POP_WIDTH,
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(15,23,32,0.12)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                color: 'var(--color-text-dim)',
                fontFamily: 'var(--font-mono)',
                fontWeight: 500,
              }}
            >
              Filter by
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-text-dim)',
                borderRadius: 4,
              }}
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <PopoverField label="Country">
              <FilterSelect
                value={countryFilter}
                onChange={onChangeCountry}
                placeholder="All countries"
                options={availableCountries}
              />
            </PopoverField>
            <PopoverField label="State / Province">
              <FilterSelect
                value={stateFilter}
                onChange={onChangeState}
                placeholder={availableStates.length ? 'All states' : '— select country first —'}
                options={availableStates}
                disabled={availableStates.length === 0}
              />
            </PopoverField>
            <PopoverField label="City">
              <FilterCity value={cityFilter} onChange={onChangeCity} />
            </PopoverField>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 16px',
              borderTop: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
            }}
          >
            <button
              onClick={onClear}
              disabled={activeCount === 0}
              style={{
                fontSize: 12,
                color: activeCount === 0 ? 'var(--color-text-dim)' : 'var(--color-accent)',
                background: 'transparent',
                border: 'none',
                cursor: activeCount === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
                padding: '4px 4px',
              }}
            >
              Clear filters
            </button>
            <button
              onClick={() => setOpen(false)}
              style={{
                fontSize: 12.5,
                fontWeight: 500,
                background: 'var(--color-accent)',
                color: '#fff',
                border: 'none',
                padding: '6px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}
            >
              Apply
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function PopoverField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span
        style={{
          fontSize: 11.5,
          color: 'var(--color-text-mid)',
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function StatusSegmented({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}) {
  const opts: { v: StatusFilter; label: string; dot?: string }[] = [
    { v: 'active', label: 'Active', dot: 'var(--color-good)' },
    { v: 'all', label: 'All' },
    { v: 'inactive', label: 'Inactive', dot: 'var(--color-text-dim)' },
  ];
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        background: 'var(--color-panel-alt)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        gap: 2,
      }}
    >
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px',
              border: 'none',
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: 'var(--font-body)',
              cursor: 'pointer',
              background: active ? 'var(--color-panel)' : 'transparent',
              color: active ? 'var(--color-text)' : 'var(--color-text-mid)',
              boxShadow: active ? '0 1px 2px rgba(15,23,32,0.06)' : 'none',
              transition: 'all 120ms',
            }}
          >
            {o.dot && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: o.dot,
                  display: 'inline-block',
                }}
              />
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
}) {
  const hasValue = !!value;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        width: '100%',
        height: 34,
        padding: '0 30px 0 11px',
        fontSize: 13,
        fontFamily: 'var(--font-body)',
        color: hasValue ? 'var(--color-text)' : 'var(--color-text-mid)',
        background:
          `var(--color-panel) url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1l4 4 4-4' stroke='%238894a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat right 11px center`,
        border: `1px solid ${hasValue ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 7,
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        outline: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontWeight: hasValue ? 500 : 400,
        transition: 'border-color 120ms',
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function FilterCity({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const hasValue = !!value;
  return (
    <input
      placeholder="City contains…"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%',
        height: 34,
        padding: '0 11px',
        fontSize: 13,
        fontFamily: 'var(--font-body)',
        color: 'var(--color-text)',
        background: 'var(--color-panel)',
        border: `1px solid ${hasValue ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 7,
        outline: 'none',
        transition: 'border-color 120ms',
      }}
    />
  );
}
